<script setup lang="ts">
/**
 * Share the open conversation with ONE colleague of the caller's organization (MAR-1417).
 *
 * Each "Create link" is a new share with its own frozen snapshot: the colleague sees the chat as it
 * is now, read-only, at their personal link, and "Continue in my chat" forks it into a conversation
 * of theirs. No notification is sent — the owner copies the link and sends it themselves.
 *
 * The list below the picker is the conversation's live shares, each revocable. It deliberately says
 * nothing about whether a colleague has continued the chat: the owner is not shown who forked.
 *
 * Mounted only while open (the header `v-if`s it), so it loads on mount and needs no watcher.
 */
import { Check, Copy, Loader2, X } from 'lucide-vue-next'
import { Button } from '~/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '~/components/ui/dialog'
import type { OrganizationMember } from '~~/shared/organizations/organization'
import type { OwnConversationShare } from '~~/shared/chat/conversationShare'
import { apiErrorMessage } from '~/utils/apiErrorMessage'

const props = defineProps<{ conversationId: string }>()
const emit = defineEmits<{ close: [] }>()

const { t } = useI18n()
const { formatDateForDisplay } = useDateHelper()
const { apiFetch } = useApi()
const { profile } = useUserProfile()

const members = ref<OrganizationMember[]>([])
const shares = ref<OwnConversationShare[]>([])
const selected = ref('')
const loading = ref(true)
const busy = ref(false)
const error = ref('')
const created = ref<OwnConversationShare | null>(null)
const copied = ref(false)

/** Everyone in the org but the caller: a share to yourself is refused by the server anyway. */
const colleagues = computed(() => members.value.filter(m => m.user_id !== profile.value?.id))

function linkFor(shareId: string): string {
  return `${window.location.origin}/app/shared/${shareId}`
}

function nameOf(share: OwnConversationShare): string {
  return share.shared_with_name || share.shared_with_email || ''
}

async function load() {
  loading.value = true
  try {
    const [m, s] = await Promise.all([
      apiFetch<OrganizationMember[]>('/api/orgs/members'),
      apiFetch<OwnConversationShare[]>(`/api/conversations/${props.conversationId}/shares`),
    ])
    members.value = m
    shares.value = s
  } catch (err) {
    error.value = apiErrorMessage(err, t('chatShare.failed'))
  } finally {
    loading.value = false
  }
}

async function createShare() {
  if (!selected.value) return
  busy.value = true
  error.value = ''
  copied.value = false
  try {
    const share = await apiFetch<OwnConversationShare>(`/api/conversations/${props.conversationId}/shares`, {
      method: 'POST',
      body: { userId: selected.value },
    })
    created.value = share
    shares.value = [share, ...shares.value]
    selected.value = ''
  } catch (err) {
    error.value = apiErrorMessage(err, t('chatShare.failed'))
  } finally {
    busy.value = false
  }
}

async function copyLink(shareId: string) {
  await navigator.clipboard.writeText(linkFor(shareId))
  copied.value = true
  window.setTimeout(() => { copied.value = false }, 1500)
}

async function revoke(share: OwnConversationShare) {
  error.value = ''
  try {
    await apiFetch(`/api/conversations/${props.conversationId}/shares/${share.id}`, { method: 'DELETE' })
    shares.value = shares.value.filter(s => s.id !== share.id)
    if (created.value?.id === share.id) created.value = null
  } catch (err) {
    error.value = apiErrorMessage(err, t('chatShare.failed'))
  }
}

onMounted(load)
</script>

<template>
  <Dialog :open="true" @update:open="v => { if (!v) emit('close') }">
    <DialogContent class="sm:max-w-md" data-testid="chat-share-dialog">
      <DialogHeader>
        <DialogTitle>{{ t('chatShare.dialogTitle') }}</DialogTitle>
        <DialogDescription>{{ t('chatShare.dialogDescription') }}</DialogDescription>
      </DialogHeader>

      <div v-if="loading" class="flex justify-center py-6">
        <Loader2 class="h-5 w-5 animate-spin text-muted-foreground" />
      </div>

      <div v-else class="space-y-4">
        <p v-if="colleagues.length === 0" class="text-sm text-muted-foreground">{{ t('chatShare.noColleagues') }}</p>
        <form v-else class="flex items-end gap-2" @submit.prevent="createShare">
          <label class="flex-1 min-w-0 space-y-1">
            <span class="text-xs font-medium text-muted-foreground">{{ t('chatShare.pickColleague') }}</span>
            <select
              v-model="selected"
              data-testid="chat-share-colleague"
              class="w-full h-9 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="" disabled>{{ t('chatShare.pickPlaceholder') }}</option>
              <option v-for="m in colleagues" :key="m.user_id" :value="m.user_id">
                {{ m.display_name ? `${m.display_name} · ${m.email}` : m.email }}
              </option>
            </select>
          </label>
          <Button type="submit" :disabled="!selected || busy" data-testid="chat-share-create">
            <Loader2 v-if="busy" class="h-4 w-4 animate-spin" />
            {{ busy ? t('chatShare.creating') : t('chatShare.create') }}
          </Button>
        </form>

        <div v-if="created" class="rounded-md border bg-muted/40 p-3 space-y-2" data-testid="chat-share-created">
          <p class="text-xs font-medium">{{ t('chatShare.linkFor', { name: nameOf(created) }) }}</p>
          <div class="flex items-center gap-2">
            <input
              :value="linkFor(created.id)"
              readonly
              data-testid="chat-share-link"
              class="flex-1 min-w-0 h-8 rounded-md border border-input bg-background px-2 text-xs"
              @focus="($event.target as HTMLInputElement).select()"
            >
            <Button type="button" size="sm" variant="outline" @click="copyLink(created.id)">
              <Check v-if="copied" class="h-3.5 w-3.5" />
              <Copy v-else class="h-3.5 w-3.5" />
              {{ copied ? t('chatShare.copied') : t('chatShare.copyLink') }}
            </Button>
          </div>
          <p class="text-xs text-muted-foreground">{{ t('chatShare.linkHint') }}</p>
        </div>

        <div v-if="shares.length > 0" class="space-y-1">
          <p class="text-xs font-medium text-muted-foreground">{{ t('chatShare.existing') }}</p>
          <ul class="divide-y rounded-md border" data-testid="chat-share-list">
            <li v-for="s in shares" :key="s.id" class="flex items-center justify-between gap-2 px-3 py-2 text-sm">
              <span class="min-w-0 truncate">
                {{ nameOf(s) }}
                <span class="text-xs text-muted-foreground">{{ t('chatShare.sharedOn', { date: formatDateForDisplay(s.created_at) }) }}</span>
              </span>
              <span class="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  class="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent-lighter transition-colors"
                  :title="t('chatShare.copyLink')"
                  @click="copyLink(s.id)"
                >
                  <Copy class="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  class="flex items-center gap-1 px-1.5 py-1 rounded-md text-xs text-destructive hover:bg-destructive/10 transition-colors"
                  @click="revoke(s)"
                >
                  <X class="h-3.5 w-3.5" />
                  {{ t('chatShare.revoke') }}
                </button>
              </span>
            </li>
          </ul>
        </div>

        <p v-if="error" class="text-sm text-destructive">{{ error }}</p>
      </div>
    </DialogContent>
  </Dialog>
</template>
